import { MudarPlanoModal } from "./MudarPlanoModal";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { empresaService } from "../../services/empresaService";
import { Empresa } from "../../types/empresa";

// Modal de Confirmação para ações sensíveis
interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative mx-auto p-6 border w-full max-w-md shadow-lg rounded-lg bg-white">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
            <svg
              className="h-6 w-6 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
          <p className="text-sm text-gray-500 mb-6">{message}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const DetalhesAssinante: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [assinante, setAssinante] = useState<Empresa | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showEditConfirmation, setShowEditConfirmation] = useState(false);
  const [showReenviarConfirmation, setShowReenviarConfirmation] =
    useState(false);
  const [isMudarPlanoModalOpen, setIsMudarPlanoModalOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    empresaService
      .detalhar(Number(id))
      .then((data) => {
        setAssinante(data);
        // Exibir setup_link caso venha junto
        if ((data as any).setup_link)
          setGeneratedLink((data as any).setup_link);
      })
      .catch((err) => {
        console.error("Erro ao carregar assinante:", err);
        setErrorMessage("Erro ao carregar detalhes do assinante.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleReenviar = async () => {
    if (!id) return;
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const resp: any = await empresaService.reenviarConvite(Number(id));
      const link =
        resp.setup_link ??
        (resp.setup_token
          ? `https://nossoapp.com/cadastrar-senha?codigo=${resp.setup_token}`
          : null);
      if (link) setGeneratedLink(link);
      setSuccessMessage(resp.detail || "Novo convite gerado com sucesso!");
    } catch (err: any) {
      console.error("Erro ao reenviar convite:", err);
      const mensagemErro =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Erro ao reenviar convite.";
      setErrorMessage(mensagemErro);
    }
  };

  const getStatusBadge = () => {
    const admin = assinante?.membros?.find((m) => m.cargo === "ADMIN");
    const status =
      admin?.status || assinante?.admin_status?.status || "Desconhecido";

    const statusColors: { [key: string]: string } = {
      ATIVO: "bg-green-100 text-green-800 border-green-200",
      PENDENTE: "bg-yellow-100 text-yellow-800 border-yellow-200",
      Pendente: "bg-yellow-100 text-yellow-800 border-yellow-200",
      INATIVO: "bg-gray-100 text-gray-800 border-gray-200",
      SUSPENSO: "bg-red-100 text-red-800 border-red-200",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium border ${
          statusColors[status] || "bg-gray-100 text-gray-800 border-gray-200"
        }`}
      >
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!assinante && errorMessage)
    return (
      <div className="container mx-auto px-4 py-8">
        <div
          className="p-4 bg-red-50 border border-red-200 rounded-lg"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <span className="text-red-600 text-2xl flex-shrink-0">⚠️</span>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-red-800 mb-1">
                Erro ao Carregar
              </h4>
              <p className="text-sm text-red-700">{errorMessage}</p>
              <button
                onClick={() => navigate("/assinantes")}
                className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
              >
                Voltar para lista de assinantes
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  if (!assinante)
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-gray-500">
          Assinante não encontrado
        </div>
      </div>
    );

  const admin = assinante.membros?.find((m) => m.cargo === "ADMIN")?.user;

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Modais de Confirmação */}
      <ConfirmationModal
        isOpen={showEditConfirmation}
        onClose={() => setShowEditConfirmation(false)}
        onConfirm={() => navigate(`/assinantes/${id}/editar`)}
        title="Confirmar Edição"
        message="Você tem certeza que deseja editar as informações deste assinante? Esta ação pode impactar os dados cadastrais e configurações."
        confirmText="Sim, Editar"
      />

      <ConfirmationModal
        isOpen={showReenviarConfirmation}
        onClose={() => setShowReenviarConfirmation(false)}
        onConfirm={handleReenviar}
        title="Reenviar Convite"
        message="Isso irá gerar um novo link de cadastro de senha. O link anterior será invalidado. Deseja continuar?"
        confirmText="Sim, Reenviar"
      />

      {/* Banners de Feedback */}
      {successMessage && (
        <div
          className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg animate-fade-in"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <span className="text-green-600 text-2xl flex-shrink-0">✅</span>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-green-800 mb-1">
                Sucesso!
              </h4>
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-600 hover:text-green-800 text-xl leading-none flex-shrink-0"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {errorMessage && assinante && (
        <div
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg animate-fade-in"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <span className="text-red-600 text-2xl flex-shrink-0">⚠️</span>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-red-800 mb-1">Erro</h4>
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
            <button
              onClick={() => setErrorMessage(null)}
              className="text-red-600 hover:text-red-800 text-xl leading-none flex-shrink-0"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Header com Breadcrumb */}
      <div className="mb-6">
        <nav className="text-sm text-gray-500 mb-2">
          <button
            onClick={() => navigate("/assinantes")}
            className="hover:text-gray-700"
          >
            Assinantes
          </button>
          <span className="mx-2">/</span>
          <span className="text-gray-900 font-medium">Detalhes</span>
        </nav>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">
                {assinante.tipo === "PJ"
                  ? assinante.razao_social
                  : admin?.nome_completo}
              </h1>
              {getStatusBadge()}
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
                {assinante.tipo}
              </span>
              <span className="text-gray-400">•</span>
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
                  />
                </svg>
                ID {assinante.id}
              </span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowEditConfirmation(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Editar
            </button>
            <button
              onClick={() => setShowReenviarConfirmation(true)}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors shadow-sm"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Reenviar Convite
            </button>
          </div>
        </div>
      </div>

      {/* Grid Principal de Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Coluna Esquerda - Dados Principais */}
        <div className="lg:col-span-2 space-y-6">
          {/* Card: Informações Básicas */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                Informações da Empresa
              </h3>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {assinante.tipo === "PJ" && (
                  <>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                        Razão Social
                      </dt>
                      <dd className="text-sm font-semibold text-gray-900">
                        {assinante.razao_social || "—"}
                      </dd>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                        Nome Fantasia
                      </dt>
                      <dd className="text-sm font-semibold text-gray-900">
                        {assinante.nome_fantasia || "—"}
                      </dd>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                        CNPJ
                      </dt>
                      <dd className="text-sm font-semibold text-gray-900">
                        {assinante.cnpj || "—"}
                      </dd>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                        Tipo
                      </dt>
                      <dd className="text-sm font-semibold text-gray-900">
                        {assinante.tipo}
                      </dd>
                    </div>
                  </>
                )}
                {assinante.inscricao_municipal && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                      Inscrição Municipal
                    </dt>
                    <dd className="text-sm font-semibold text-gray-900">
                      {assinante.inscricao_municipal}
                    </dd>
                  </div>
                )}
                {assinante.inscricao_estadual && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                      Inscrição Estadual
                    </dt>
                    <dd className="text-sm font-semibold text-gray-900">
                      {assinante.inscricao_estadual}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Card: Administrador */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                Administrador
              </h3>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                    Nome Completo
                  </dt>
                  <dd className="text-sm font-semibold text-gray-900">
                    {admin?.nome_completo || "Não informado"}
                  </dd>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                    Email
                  </dt>
                  <dd className="text-sm font-semibold text-gray-900 break-all">
                    {admin?.email || "Não informado"}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Card: Endereço */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Localização
              </h3>
            </div>
            <div className="p-6">
              {assinante.endereco?.logradouro ? (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                      Logradouro
                    </dt>
                    <dd className="text-sm font-semibold text-gray-900">
                      {assinante.endereco.logradouro.nome}
                    </dd>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                      Número
                    </dt>
                    <dd className="text-sm font-semibold text-gray-900">
                      {assinante.endereco.numero}
                    </dd>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                      Bairro
                    </dt>
                    <dd className="text-sm font-semibold text-gray-900">
                      {assinante.endereco.logradouro.bairro?.nome_original ||
                        assinante.endereco.logradouro.bairro?.nome}
                    </dd>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                      Cidade / UF
                    </dt>
                    <dd className="text-sm font-semibold text-gray-900">
                      {assinante.endereco.logradouro.cidade?.nome_original ||
                        assinante.endereco.logradouro.cidade?.nome}{" "}
                      / {assinante.endereco.logradouro.cidade?.uf?.sigla}
                    </dd>
                  </div>
                  {assinante.endereco.complemento && (
                    <div className="bg-gray-50 p-4 rounded-lg sm:col-span-2">
                      <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                        Complemento
                      </dt>
                      <dd className="text-sm font-semibold text-gray-900">
                        {assinante.endereco.complemento}
                      </dd>
                    </div>
                  )}
                </dl>
              ) : (
                <p className="text-sm text-gray-500">Endereço não cadastrado</p>
              )}
            </div>
          </div>
        </div>

        {/* Coluna Direita - Sidebar */}
        <div className="space-y-6">
          {/* Card: Status e Atividade */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Status da Conta
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">
                  Status Atual
                </span>
                {getStatusBadge()}
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">
                  ID da Conta
                </span>
                <span className="text-sm font-mono font-semibold text-gray-900">
                  #{assinante.id}
                </span>
              </div>
              {assinante.franquia_gerenciadora && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">
                    Franquia
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {assinante.franquia_gerenciadora.nome_fantasia}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Card: Plano/Assinatura */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-cyan-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Plano & Assinatura
              </h3>
            </div>
            <div className="p-6">
              {assinante.plano ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Plano Atual</p>
                    <p className="text-lg font-bold text-gray-900">{assinante.plano.nome}</p>
                    <p className="text-sm text-gray-600">{assinante.plano.descricao}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <p className="text-sm font-medium text-gray-700">Valor</p>
                      <p className="text-lg font-semibold text-gray-900">R$ {parseFloat(assinante.plano.valor).toFixed(2)} / mês</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsMudarPlanoModalOpen(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h5M4 12a8 8 0 018-8v0a8 8 0 018 8v0a8 8 0 01-8 8m-4-4h4"></path>
                    </svg>
                    Mudar Plano
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg
                    className="w-12 h-12 text-gray-300 mx-auto mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                  <p className="text-sm text-gray-500 mb-2">Nenhum plano ativo</p>
                  <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                    Associar Plano
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Card: Últimos Acessos */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-violet-50 to-purple-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-violet-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Atividade Recente
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5"></div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-900">
                      Conta Criada
                    </p>
                    <p className="text-xs text-gray-500">Aguardando ativação</p>
                  </div>
                </div>
                <div className="text-center py-4">
                  <p className="text-xs text-gray-400">
                    Nenhuma atividade registrada
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MudarPlanoModal
        isOpen={isMudarPlanoModalOpen}
        onClose={() => setIsMudarPlanoModalOpen(false)}
        empresaId={assinante.id}
      />

      {/* Painel do Link de Configuração */}
      {generatedLink && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-green-200">
            <h3 className="text-lg font-semibold text-green-800 flex items-center gap-2">
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Link de Configuração de Senha Gerado
            </h3>
          </div>
          <div className="p-6">
            <p className="text-sm text-green-700 mb-4">
              Envie o link abaixo para o administrador configurar a senha
              inicial da conta.
            </p>
            <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-300">
              <div className="flex-1 overflow-hidden">
                <code className="text-sm font-mono text-green-800 break-all">
                  {generatedLink}
                </code>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(generatedLink);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2 text-sm font-medium whitespace-nowrap"
              >
                {copied ? (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Copiado!
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                      />
                    </svg>
                    Copiar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
